(() => {
  if (!customElements.get('accessory-fold')) {
    customElements.define('accessory-fold', class extends HTMLElement {
      connectedCallback() {
        const preview = this.querySelector('.oxaf__preview-card');
        if (!preview || this.resizeObserver) return;
        this.resizeObserver = new ResizeObserver(() => {
          const height = preview.getBoundingClientRect().height;
          if (height) this.style.setProperty('--oxaf-peek-height', `${Math.round(height / 2)}px`);
        });
        this.resizeObserver.observe(preview);
      }
      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
      }
    });
  }
  if (customElements.get('accessory-fold-item')) return;
  customElements.define('accessory-fold-item', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('.oxaf__form');
      const data = this.querySelector('[data-ox-variants]');
      if (!this.form || !data) return;
      try { this.variants = JSON.parse(data.textContent); } catch { return; }
      this.idInput = this.form.querySelector('[data-ox-id]');
      this.options = Array.from(this.form.querySelectorAll('[data-ox-option]'));
      const initialVariant = this.variants.find(item => String(item.id) === this.idInput.value);
      if (initialVariant) this.options.forEach((option, index) => { option.value = initialVariant.options[index]; });
      this.button = this.form.querySelector('.oxaf__add');
      this.buttonText = this.button.querySelector('[data-ox-button-text]');
      this.status = this.form.querySelector('[data-ox-status]');
      this.onChange = this.onChange || this.handleChange.bind(this);
      this.onSubmit = this.onSubmit || this.handleSubmit.bind(this);
      this.form.addEventListener('change', this.onChange);
      this.form.addEventListener('submit', this.onSubmit);
      this.ready = true;
      this.classList.add('is-ready');
      const controls = this.querySelector('.oxaf__options');
      if (controls) controls.hidden = false;
      this.updateVariant();
    }
    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('change', this.onChange);
        this.form.removeEventListener('submit', this.onSubmit);
      }
      window.clearTimeout(this.resetTimer);
      this.ready = false;
    }
    handleChange(event) {
      if (!event.target.matches('[data-ox-option]')) return;
      window.clearTimeout(this.resetTimer);
      this.setStatus('');
      this.updateVariant();
    }
    updateVariant() {
      const values = this.options.map(option => option.value);
      const variant = this.options.length
        ? this.variants.find(item => item.options.every((value, index) => value === values[index]))
        : this.variants.find(item => String(item.id) === this.idInput.value);
      this.variant = variant;
      this.idInput.value = variant ? String(variant.id) : '';
      const price = this.querySelector('[data-ox-price]');
      const compare = this.querySelector('[data-ox-compare]');
      if (price) price.textContent = variant ? variant.price : '—';
      if (compare) {
        compare.hidden = !variant?.comparePrice;
        compare.querySelector('s').textContent = variant?.comparePrice || '';
      }
      const image = this.querySelector('.oxaf__media img');
      if (image && variant?.image && image.getAttribute('src') !== variant.image) {
        image.removeAttribute('srcset');
        image.src = variant.image;
      }
      const url = new URL(this.dataset.productUrl, window.location.origin);
      if (variant) url.searchParams.set('variant', variant.id);
      this.querySelectorAll('[data-ox-link]').forEach(link => { link.href = url.pathname + url.search; });
      this.setLoading(Boolean(this.isLoading));
    }
    setLoading(loading) {
      this.isLoading = loading;
      this.form.setAttribute('aria-busy', String(loading));
      this.options.forEach(option => { option.disabled = loading; });
      this.button.disabled = loading || !this.variant?.available;
      this.buttonText.textContent = loading ? this.dataset.labelAdding
        : !this.variant ? this.dataset.labelUnavailable
        : !this.variant.available ? this.dataset.labelSoldOut
        : this.dataset.labelAdd;
      this.button.setAttribute('aria-label', this.buttonText.textContent + ': ' + this.querySelector('.oxaf__title').textContent.trim());
    }
    setStatus(message, state = '') {
      this.status.textContent = message;
      this.status.dataset.state = state;
      this.status.setAttribute('role', state === 'error' ? 'alert' : 'status');
      this.status.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
    }
    async handleSubmit(event) {
      event.preventDefault();
      if (this.isLoading || !this.variant?.available || this.button.disabled) return;
      const variantId = this.variant.id;
      window.clearTimeout(this.resetTimer);
      this.setStatus('');
      this.setLoading(true);
      let added = false;
      try {
        const request = {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        };
        const coordinator = window.LumaryCartCoordinator;
        const usesCoordinator = Boolean(coordinator && typeof coordinator.enqueueFetch === 'function');
        const ticket = usesCoordinator ? coordinator.enqueueFetch(this.dataset.cartAddUrl, request, {
          source: 'accessory-fold', showCart: true, trigger: this.button, resolveFailureResult: true
        }) : null;
        const response = usesCoordinator ? await ticket.mutation : await fetch(this.dataset.cartAddUrl, request);
        const result = await response.json().catch(() => ({}));
        const failure = usesCoordinator && typeof coordinator.classifyFetchResponse === 'function'
          ? coordinator.classifyFetchResponse(response) : '';
        if (!response.ok || failure) {
          const message = result.description || result.message;
          throw new Error(typeof message === 'string' ? message : this.dataset.labelError);
        }
        added = true;
        let cart = null;
        try { cart = usesCoordinator ? await ticket.refreshed : await this.refreshCart(); } catch {}
        this.setStatus(this.dataset.labelSuccess, 'success');
        this.dispatchEvent(new CustomEvent('accessory:added', { bubbles: true, detail: { item: result, cart, variantId } }));
      } catch (error) {
        this.setStatus(error.message || this.dataset.labelError, 'error');
      } finally {
        this.setLoading(false);
        if (added) {
          this.buttonText.textContent = this.dataset.labelAdded;
          this.button.setAttribute('aria-label', this.dataset.labelAdded + ': ' + this.querySelector('.oxaf__title').textContent.trim());
          this.resetTimer = window.setTimeout(() => this.setLoading(false), 1800);
        }
      }
    }
    async refreshCart() {
      const drawerEnabled = typeof cartDrawerEnable !== 'undefined' && cartDrawerEnable;
      if (drawerEnabled && typeof requestCartDrawer === 'function' && window.jQuery) {
        return requestCartDrawer(true, window.jQuery(this.button), false);
      }
      const response = await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach(element => {
        element.textContent = cart.item_count > 0 && cart.item_count < 100 ? cart.item_count : '';
        element.hidden = cart.item_count === 0;
      });
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      return cart;
    }
  });
})();
